import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Autocomplete,
} from '@mui/material'
import { Add, Delete, Edit } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useRoles, useCreateRole, useDeleteRole, useSetRolePermissions, Role } from '../../hooks/useUsers'

const ALL_PERMISSIONS = [
  'view_device', 'manage_device', 'delete_device',
  'view_sensors', 'manage_sensors',
  'view_alerts', 'manage_alerts', 'acknowledge_alert',
  'view_rules', 'manage_rules',
  'view_analytics', 'export_analytics',
  'view_users', 'manage_users',
  'view_tenants', 'manage_tenants',
  'view_billing', 'manage_billing',
]

export default function RolesPage() {
  const { t } = useTranslation()
  const { data: roles = [], isLoading } = useRoles()
  const createRole = useCreateRole()
  const deleteRole = useDeleteRole()
  const setPermissions = useSetRolePermissions()

  const [createOpen, setCreateOpen] = useState(false)
  const [editRole, setEditRole] = useState<Role | null>(null)
  const [newRoleName, setNewRoleName] = useState('')
  const [permissions, setPermissions2] = useState<string[]>([])
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  const handleCreate = async () => {
    await createRole.mutateAsync({ name: newRoleName })
    setCreateOpen(false)
    setNewRoleName('')
  }

  const handleSavePermissions = async () => {
    if (!editRole) return
    await setPermissions.mutateAsync({ id: editRole.id, permissions })
    analytics.track(EVENTS.ROLE_UPDATED, { role_id: editRole.id, role_name: editRole.name, permission_count: permissions.length })
    setEditRole(null)
  }

  const openEditPermissions = (role: Role) => {
    setEditRole(role)
    setPermissions2(role.permissions ?? [])
  }

  return (
    <Box>
      <PageHeader
        title={t('roles.title')}
        subtitle={t('roles.createRole')}
        breadcrumbs={[{ label: t('nav.identity') }, { label: t('nav.roles') }]}
        action={
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            {t('roles.createRole')}
          </Button>
        }
      />

      {isLoading ? null : (
        <Grid container spacing={2}>
          {roles.map((role) => (
            <Grid item xs={12} sm={6} md={4} key={role.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {role.name}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {(role.permissions ?? []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('roles.noPermissions')}
                      </Typography>
                    ) : (
                      (role.permissions ?? []).map((p) => (
                        <Chip key={p} label={p} size="small" variant="outlined" />
                      ))
                    )}
                  </Stack>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip title={t('roles.editPermissions')}>
                    <IconButton size="small" onClick={() => openEditPermissions(role)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('roles.deleteRole')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(role.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Role Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('roles.createRole')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label={t('common.name')}
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newRoleName || createRole.isPending}
          >
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog open={!!editRole} onClose={() => setEditRole(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('roles.editPermissions')} — {editRole?.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Autocomplete
            multiple
            options={ALL_PERMISSIONS}
            value={permissions}
            onChange={(_, v) => setPermissions2(v)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label={t('roles.editPermissions')} placeholder={t('roles.addPermission')} />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRole(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSavePermissions}
            disabled={setPermissions.isPending}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('roles.deleteRole')}
        message={t('roles.deleteConfirm')}
        onConfirm={() => {
          deleteRole.mutate(deleteTarget!)
          setDeleteTarget(null)
        }}
        onClose={() => setDeleteTarget(null)}
        loading={deleteRole.isPending}
      />
    </Box>
  )
}
